import {
  ControlPosition,
  Map,
  MapControl,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from "@googlemaps/markerclusterer";
import type {
  Renderer,
  Cluster,
  ClusterStats,
} from "@googlemaps/markerclusterer";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import { reportMapsClientEvent } from "~/lib/maps/client-telemetry";
import markerIcon from "~/assets/marker.png";
import clusterIcon from "~/assets/cluster.png"; // Your cluster image
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import {
  LocationWithImages,
  MarkerCoordinate,
} from "~/lib/server-functions/get-locations";
import {
  FetchingMarkerInfoWindowContent,
  LocationInfoWindowContent,
} from "./LocationInfoWindowContent";

interface MapViewProps {
  markers?: MarkerCoordinate[];
  pageLocations?: LocationWithImages[];
  currentPageIds?: Set<number>;
  center: { lat: number; lng: number } | null;
  bounds?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  } | null;
  isMapFilterActive: boolean;
  isMaximized: boolean;
  /** When false the host hides the map with CSS; used to resize after re-show. */
  isVisible?: boolean;
  activeMarkerId?: number;
  hoveredMarkerId?: number;
  onBoundsChange?: (b: google.maps.LatLngBounds) => void;
  onZoomChange?: (zoom: number) => void;
  onCollapse?: () => void;
  onMapFilterChange?: (isActive: boolean) => void;
  onMaximizeChange: (isMaximized: boolean) => void;
  onActiveMarkerIdChange?: (id: number | undefined) => void;
  onMarkerClick?: (locationId: number) => void;
  onMapIdle?: () => void;
}

// Custom renderer for cluster icons
class CustomRenderer implements Renderer {
  private onClusterRendered?: (
    cluster: Cluster,
    marker: google.maps.marker.AdvancedMarkerElement,
  ) => void;

  constructor(
    onClusterRendered?: (
      cluster: Cluster,
      marker: google.maps.marker.AdvancedMarkerElement,
    ) => void,
  ) {
    this.onClusterRendered = onClusterRendered;
  }

  public render(
    cluster: Cluster,
    stats: ClusterStats,
    map: google.maps.Map,
  ): google.maps.marker.AdvancedMarkerElement {
    const count = cluster.count;

    // Create cluster icon container
    const container = document.createElement("div");
    container.style.position = "relative";
    container.style.width = "40px";
    container.style.height = "40px";

    // Add custom cluster image
    const img = document.createElement("img");
    img.src = clusterIcon;
    img.alt = "";
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.objectFit = "contain";

    // Add count label
    const label = document.createElement("div");
    label.textContent = String(count);
    label.style.position = "absolute";
    label.style.top = "50%";
    label.style.left = "50%";
    label.style.transform = "translate(-50%, -50%) translateY(-3px)";
    label.style.color = "white";
    label.style.fontWeight = "bold";
    label.style.fontSize = "14px";
    container.appendChild(img);
    container.appendChild(label);

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: cluster.position,
      content: container,
      zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
    });

    // Store reference to container for later updates
    (marker as any).__clusterContainer = container;
    (marker as any).__cluster = cluster;

    // Notify that cluster was rendered
    if (this.onClusterRendered) {
      this.onClusterRendered(cluster, marker);
    }

    return marker;
  }
}

/**
 * Search map. Parent must wrap with `APIProvider` and should keep this mounted
 * after first open (hide with CSS) so collapsing/reopening does not bill another
 * Dynamic Map load. `reuseMaps` is a second line of defense if remount happens.
 */
export const MapView = ({
  markers,
  pageLocations,
  currentPageIds,
  center,
  bounds,
  isMapFilterActive,
  isMaximized,
  isVisible = true,
  activeMarkerId,
  hoveredMarkerId,
  onBoundsChange,
  onZoomChange,
  onCollapse,
  onMapFilterChange,
  onMaximizeChange,
  onActiveMarkerIdChange,
  onMarkerClick,
  onMapIdle,
}: MapViewProps) => {
  const defaultCenter = {
    lat: 48.13586769455149,
    lng: 11.569643059654094,
  };

  const [userLocation, setUserLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Request user's location if center is not already set via geocoded location
  useEffect(() => {
    if (center !== null) return; // Don't request if center is already set

    if (!navigator.geolocation) return; // Geolocation not available

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        // Silently fail if user denies or error occurs
        console.debug("Geolocation error:", error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  }, [center]);

  // Use user location if available and center is not set, otherwise use default
  const initialCenter = center ?? userLocation ?? defaultCenter;

  return (
    <Map
      mapId={"3b1bbeabe7a000677f24355e"}
      style={{ width: "100%", height: "100%" }}
      defaultCenter={initialCenter}
      defaultZoom={10}
      gestureHandling="greedy"
      disableDefaultUI
      zoomControl={true}
      reuseMaps={true}
    >
      <MapMountTelemetry />
      <MapVisibilityHandler isVisible={isVisible} />
      <MapController center={center} bounds={bounds} />
      <MapEventListener
        onBoundsChange={onBoundsChange}
        onZoomChange={onZoomChange}
        onMapIdle={onMapIdle}
      />
      <MapControl position={ControlPosition.TOP_LEFT}>
        <div className="p-3">
          <Button
            onClick={() => onMaximizeChange(!isMaximized)}
            variant={"map"}
            size={"map"}
          >
            {isMaximized ? <ChevronRight /> : <ChevronLeft />}
          </Button>
        </div>
      </MapControl>
      <MapControl position={ControlPosition.TOP_RIGHT}>
        <div className="flex gap-6 items-center p-3">
          <div className="bg-white/90 h-9 rounded-md flex items-center px-2">
            <Label>
              <Checkbox
                checked={isMapFilterActive}
                onCheckedChange={onMapFilterChange}
              />
              Beim Bewegen der Karte suchen
            </Label>
          </div>
          <Button variant={"map"} size={"map"} onClick={onCollapse}>
            <X />
          </Button>
        </div>
      </MapControl>
      <MarkerClusterComponent
        markers={markers}
        pageLocations={pageLocations}
        currentPageIds={currentPageIds}
        activeMarkerId={activeMarkerId}
        hoveredMarkerId={hoveredMarkerId}
        onActiveMarkerIdChange={onActiveMarkerIdChange}
        onMarkerClick={onMarkerClick}
      />
    </Map>
  );
};

/**
 * Reports that a real Google Map instance exists. Diagnostics only — Google
 * Cloud is authoritative for Dynamic Map billing.
 */
function MapMountTelemetry() {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    reportMapsClientEvent("map_mounted", "search_map");
  }, [map]);

  return null;
}

/** After CSS hide/show, Google Maps needs a resize so tiles fill the container. */
function MapVisibilityHandler({ isVisible }: { isVisible: boolean }) {
  const map = useMap();
  const wasVisibleRef = useRef(isVisible);

  useEffect(() => {
    if (!map) return;
    if (isVisible && !wasVisibleRef.current) {
      google.maps.event.trigger(map, "resize");
      const center = map.getCenter();
      if (center) {
        map.setCenter(center);
      }
    }
    wasVisibleRef.current = isVisible;
  }, [map, isVisible]);

  return null;
}

/**
 * Helper function to offset markers with duplicate coordinates
 * Arranges them in a small circle pattern so all are visible and clickable
 */
function offsetDuplicateCoordinates(
  locations: MarkerCoordinate[],
): Array<MarkerCoordinate & { displayLat: number; displayLng: number }> {
  const COORDINATE_TOLERANCE = 0.00001; // ~1 meter tolerance for "same" coordinates
  const OFFSET_RADIUS = 0.00003; // ~3 meters offset radius

  // Group locations by coordinates
  const coordinateGroups: Record<string, MarkerCoordinate[]> = {};

  for (const location of locations) {
    // Round coordinates to tolerance level for grouping
    const roundedLat = Math.round(location.lat / COORDINATE_TOLERANCE);
    const roundedLng = Math.round(location.lng / COORDINATE_TOLERANCE);
    const key = `${roundedLat},${roundedLng}`;

    if (!coordinateGroups[key]) {
      coordinateGroups[key] = [];
    }
    coordinateGroups[key].push(location);
  }

  // Apply offsets to duplicates
  const result: Array<
    MarkerCoordinate & { displayLat: number; displayLng: number }
  > = [];

  for (const group of Object.values(coordinateGroups)) {
    if (group.length === 1) {
      // No offset needed for single marker
      result.push({
        ...group[0],
        displayLat: group[0].lat,
        displayLng: group[0].lng,
      });
    } else {
      // Multiple markers at same location - offset in circle pattern
      const angleStep = (2 * Math.PI) / group.length;

      group.forEach((location: MarkerCoordinate, index: number) => {
        const angle = index * angleStep;
        const offsetLat = OFFSET_RADIUS * Math.cos(angle);
        const offsetLng = OFFSET_RADIUS * Math.sin(angle);

        result.push({
          ...location,
          displayLat: location.lat + offsetLat,
          displayLng: location.lng + offsetLng,
        });
      });
    }
  }

  return result;
}

function openInfoWindowSafely(
  map: google.maps.Map,
  infoWindow: google.maps.InfoWindow,
  marker: google.maps.marker.AdvancedMarkerElement,
  content: HTMLElement,
) {
  const tryOpen = () => {
    if (marker.map === map) {
      infoWindow.setContent(content);
      infoWindow.open({ map, anchor: marker });
    } else {
      requestAnimationFrame(tryOpen);
    }
  };

  requestAnimationFrame(tryOpen);
}

/**
 * MarkerClusterComponent with InfoWindow support
 *
 * Hybrid Solution Implementation:
 * - Uses Google Maps InfoWindow for positioning and behavior
 * - Renders React components inside InfoWindow using createRoot
 * - Provides full styling control of content while leveraging Google Maps features
 *
 * How it works:
 * 1. Markers are created with click listeners
 * 2. On click, a React component is rendered into a DOM container
 * 3. The container is set as InfoWindow content
 * 4. InfoWindow handles positioning, panning, and closing
 */
function MarkerClusterComponent({
  markers,
  pageLocations,
  currentPageIds,
  activeMarkerId,
  hoveredMarkerId,
  onActiveMarkerIdChange,
  onMarkerClick,
}: {
  markers?: MarkerCoordinate[];
  pageLocations?: LocationWithImages[];
  currentPageIds?: Set<number>;
  activeMarkerId?: number;
  hoveredMarkerId?: number;
  onActiveMarkerIdChange?: (id: number | undefined) => void;
  onMarkerClick?: (locationId: number) => void;
}) {
  const map = useMap();
  const markerLibrary = useMapsLibrary("marker");

  const markerMapRef = useRef<
    Map<number, google.maps.marker.AdvancedMarkerElement>
  >(new globalThis.Map());
  const clustererRef = useRef<InstanceType<typeof MarkerClusterer> | null>(
    null,
  );
  const clusterMarkersRef = useRef<
    Map<Cluster, google.maps.marker.AdvancedMarkerElement>
  >(new globalThis.Map());
  const markerToClusterRef = useRef<Map<number, Cluster>>(new globalThis.Map());
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const reactRootRef = useRef<Root | null>(null);
  const selectedMarkerRef = useRef<MarkerCoordinate | null>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const activeClusterRef = useRef<Cluster | null>(null);
  const activeMarkerIdRef = useRef<number | undefined>(activeMarkerId);
  const currentPageIdsRef = useRef(currentPageIds);
  const pageLocationsRef = useRef(pageLocations);
  const prevHoveredIdRef = useRef<number | undefined>(undefined);
  const prevActiveDisplayRef = useRef<{
    id: number;
    clustered: boolean;
  } | null>(null);
  const markerDataMapRef = useRef<Map<number, MarkerCoordinate>>(
    new globalThis.Map(),
  );

  useEffect(() => {
    currentPageIdsRef.current = currentPageIds;
  }, [currentPageIds]);
  useEffect(() => {
    pageLocationsRef.current = pageLocations;
  }, [pageLocations]);

  // Hide Google's default close button and remove padding - add CSS once
  useEffect(() => {
    if (!styleRef.current) {
      const style = document.createElement("style");
      style.textContent = `
        .gm-style-iw-chr {
          display: none !important;
        }
        .gm-style-iw-c {
          padding: 0 !important;
        }
        .gm-style-iw-d {
          padding: 0 !important;
          overflow: hidden !important;
        }
      `;
      document.head.appendChild(style);
      styleRef.current = style;
    }

    return () => {
      // Cleanup style on unmount
      if (styleRef.current && styleRef.current.parentNode) {
        styleRef.current.parentNode.removeChild(styleRef.current);
        styleRef.current = null;
      }
    };
  }, []);

  // Cleanup InfoWindow when component unmounts
  useEffect(() => {
    return () => {
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      // Defer unmounting to avoid race condition
      if (reactRootRef.current) {
        const rootToUnmount = reactRootRef.current;
        reactRootRef.current = null;
        setTimeout(() => {
          rootToUnmount.unmount();
        }, 0);
      }
    };
  }, []);

  const openInfoWindow = useCallback(
    (
      markerEl: google.maps.marker.AdvancedMarkerElement,
      markerData: MarkerCoordinate,
    ) => {
      if (!map) return;

      // Close previous InfoWindow if open
      if (infoWindowRef.current) infoWindowRef.current.close();

      // Defer unmounting to avoid race condition with React rendering
      if (reactRootRef.current) {
        const rootToUnmount = reactRootRef.current;
        reactRootRef.current = null;
        setTimeout(() => rootToUnmount.unmount(), 0);
      }

      // Create container for React content
      const contentContainer = document.createElement("div");
      contentContainer.style.position = "relative";

      const root = createRoot(contentContainer);
      reactRootRef.current = root;

      const onClose = () => {
        if (infoWindowRef.current) infoWindowRef.current.close();
        if (reactRootRef.current) {
          const rootToUnmount = reactRootRef.current;
          reactRootRef.current = null;
          setTimeout(() => rootToUnmount.unmount(), 0);
        }
        selectedMarkerRef.current = null;
        onActiveMarkerIdChange?.(undefined);
      };

      const fullLocation = currentPageIdsRef.current?.has(markerData.id)
        ? pageLocationsRef.current?.find((l) => l.id === markerData.id)
        : undefined;

      if (fullLocation) {
        root.render(
          <LocationInfoWindowContent
            location={fullLocation}
            onClose={onClose}
          />,
        );
      } else {
        root.render(
          <FetchingMarkerInfoWindowContent
            marker={markerData}
            onClose={onClose}
          />,
        );
      }

      if (!infoWindowRef.current) {
        infoWindowRef.current = new google.maps.InfoWindow({
          disableAutoPan: false,
          pixelOffset: new google.maps.Size(0, -10),
        });
      }
      openInfoWindowSafely(
        map,
        infoWindowRef.current,
        markerEl,
        contentContainer,
      );

      selectedMarkerRef.current = markerData;
    },
    [map, onActiveMarkerIdChange],
  );

  useEffect(() => {
    if (!map || !markerLibrary || !markers) return;

    // Offset markers with duplicate coordinates
    const locationsWithOffsets = offsetDuplicateCoordinates(markers);

    // Create InfoWindow instance (reused for all markers)
    // InfoWindow is available directly from google.maps namespace
    if (!infoWindowRef.current) {
      infoWindowRef.current = new google.maps.InfoWindow({
        disableAutoPan: false, // Auto-pan to show InfoWindow
        pixelOffset: new google.maps.Size(0, -10), // Offset from marker
      });
    }

    // Create marker elements with click handlers.
    // Do NOT set `map` on individual markers — let the clusterer manage visibility.
    // This avoids a flash of 6700+ markers before clustering kicks in.
    const newMarkers = locationsWithOffsets.map((location) => {
      const img = document.createElement("img");
      img.src = markerIcon;
      img.alt = "";
      img.style.width = "25px";
      img.style.height = "auto";
      img.style.objectFit = "cover";
      img.style.cursor = "pointer";
      img.title = location.name;

      const marker = new markerLibrary.AdvancedMarkerElement({
        position: { lat: location.displayLat, lng: location.displayLng },
        content: img,
      });

      (marker as any).__locationId = location.id;

      marker.addListener("click", () => {
        openInfoWindow(marker, location);
        onMarkerClick?.(location.id);
      });
      markerMapRef.current.set(location.id, marker);

      return marker;
    });

    markerDataMapRef.current = new globalThis.Map(
      markers.map((m) => [m.id, m]),
    );

    // Initialize clusterer with custom renderer
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }

    // Clear previous mappings
    clusterMarkersRef.current.clear();
    markerToClusterRef.current.clear();

    // Callback to store cluster information when rendered
    const onClusterRendered = (
      cluster: Cluster,
      clusterMarker: google.maps.marker.AdvancedMarkerElement,
    ) => {
      clusterMarkersRef.current.set(cluster, clusterMarker);

      cluster.markers.forEach((marker) => {
        const id = (marker as any).__locationId as number | undefined;
        if (id !== undefined) {
          markerToClusterRef.current.set(id, cluster);
        }
      });
    };

    clustererRef.current = new MarkerClusterer({
      map,
      markers: newMarkers,
      renderer: new CustomRenderer(onClusterRendered),
      algorithm: new SuperClusterAlgorithm({ radius: 100 }),
    });

    // The renderer callback will be called automatically when clusters are created
    // Cluster mapping updates are handled by the onClusterRendered callback
    // InfoWindow repositioning on cluster changes is handled in a separate effect

    // Cleanup
    return () => {
      newMarkers.forEach((marker) => {
        // Remove all listeners
        google.maps.event.clearInstanceListeners(marker);
        marker.map = null;
      });
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
      clusterMarkersRef.current.clear();
      markerToClusterRef.current.clear();
      // Close InfoWindow on cleanup
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      // Defer unmounting to avoid race condition
      if (reactRootRef.current) {
        const rootToUnmount = reactRootRef.current;
        reactRootRef.current = null;
        setTimeout(() => {
          rootToUnmount.unmount();
        }, 0);
      }
    };
  }, [map, markerLibrary, markers, openInfoWindow]);

  // Update activeMarkerId ref when it changes
  useEffect(() => {
    activeMarkerIdRef.current = activeMarkerId;
  }, [activeMarkerId]);

  const resetPrevActiveDisplay = useCallback(() => {
    const prev = prevActiveDisplayRef.current;
    if (!prev) return;
    if (prev.clustered) {
      const prevCluster = markerToClusterRef.current.get(prev.id);
      if (prevCluster) {
        const cm = clusterMarkersRef.current.get(prevCluster);
        if (cm) {
          const c = (cm as any).__clusterContainer as HTMLElement;
          if (c) c.classList.remove("marker-bounce");
          cm.zIndex = undefined;
        }
      }
    } else {
      const pm = markerMapRef.current.get(prev.id);
      if (pm) {
        const img = pm.content as HTMLImageElement;
        if (img) img.classList.remove("marker-bounce");
        pm.zIndex = undefined;
      }
    }
    prevActiveDisplayRef.current = null;
  }, []);

  const updateActiveMarkerDisplay = useCallback(() => {
    const currentActiveMarkerId = activeMarkerIdRef.current;
    if (!currentActiveMarkerId) return;

    const markerData = markerDataMapRef.current.get(currentActiveMarkerId);
    if (!markerData) return;

    resetPrevActiveDisplay();

    const cluster = markerToClusterRef.current.get(currentActiveMarkerId);

    if (cluster) {
      const clusterMarker = clusterMarkersRef.current.get(cluster);
      if (clusterMarker) {
        activeClusterRef.current = cluster;
        const container = (clusterMarker as any)
          .__clusterContainer as HTMLElement;
        if (container) container.classList.add("marker-bounce");
        clusterMarker.zIndex = 9999;
        prevActiveDisplayRef.current = {
          id: currentActiveMarkerId,
          clustered: true,
        };
        openInfoWindow(clusterMarker, markerData);
      } else {
        const marker = markerMapRef.current.get(currentActiveMarkerId);
        if (marker) {
          const img = marker.content as HTMLImageElement;
          if (img) img.classList.add("marker-bounce");
          marker.zIndex = 9999;
          prevActiveDisplayRef.current = {
            id: currentActiveMarkerId,
            clustered: false,
          };
          openInfoWindow(marker, markerData);
        }
      }
    } else {
      const marker = markerMapRef.current.get(currentActiveMarkerId);
      if (marker) {
        const img = marker.content as HTMLImageElement;
        if (img) img.classList.add("marker-bounce");
        marker.zIndex = 9999;
        prevActiveDisplayRef.current = {
          id: currentActiveMarkerId,
          clustered: false,
        };
        openInfoWindow(marker, markerData);
      }
    }
  }, [openInfoWindow, resetPrevActiveDisplay]);

  // Handle hover effect on markers — only touch the previous and current marker
  useEffect(() => {
    const prevId = prevHoveredIdRef.current;
    if (prevId) {
      const prevCluster = markerToClusterRef.current.get(prevId);
      if (prevCluster) {
        const cm = clusterMarkersRef.current.get(prevCluster);
        if (cm) {
          const c = (cm as any).__clusterContainer as HTMLElement;
          if (c) c.classList.remove("marker-hover-bounce");
        }
      } else {
        const pm = markerMapRef.current.get(prevId);
        if (pm) {
          const img = pm.content as HTMLImageElement;
          if (img) img.classList.remove("marker-hover-bounce");
        }
      }
    }

    prevHoveredIdRef.current = hoveredMarkerId;

    if (hoveredMarkerId && hoveredMarkerId !== activeMarkerId) {
      requestAnimationFrame(() => {
        const cluster = markerToClusterRef.current.get(hoveredMarkerId);
        if (cluster) {
          const clusterMarker = clusterMarkersRef.current.get(cluster);
          if (clusterMarker) {
            const container = (clusterMarker as any)
              .__clusterContainer as HTMLElement;
            if (container) container.classList.add("marker-hover-bounce");
          }
        } else {
          const marker = markerMapRef.current.get(hoveredMarkerId);
          if (marker) {
            const img = marker.content as HTMLImageElement;
            if (img) img.classList.add("marker-hover-bounce");
          }
        }
      });
    }
  }, [hoveredMarkerId, activeMarkerId]);

  useEffect(() => {
    if (!activeMarkerId) {
      resetPrevActiveDisplay();
      activeClusterRef.current = null;
      infoWindowRef.current?.close();
      return;
    }

    const timeoutId = setTimeout(updateActiveMarkerDisplay, 50);
    return () => clearTimeout(timeoutId);
  }, [activeMarkerId, updateActiveMarkerDisplay, resetPrevActiveDisplay]);

  // Listen for map idle events to update InfoWindow position when clusters change
  useEffect(() => {
    if (!map || !activeMarkerId) return;

    const idleListener = map.addListener("idle", () => {
      // Wait for clusters to be re-rendered and mapping to be updated
      setTimeout(() => {
        if (
          activeMarkerIdRef.current === activeMarkerId &&
          selectedMarkerRef.current
        ) {
          // Always call updateActiveMarkerDisplay to ensure everything is correct
          // This will update the bounce state and reposition the InfoWindow correctly
          // even if the cluster structure has changed
          updateActiveMarkerDisplay();
        }
      }, 200); // Increased delay to ensure cluster mapping is updated
    });

    return () => {
      google.maps.event.removeListener(idleListener);
    };
  }, [map, activeMarkerId, updateActiveMarkerDisplay]);

  return null;
}

function MapController({
  center,
  bounds,
}: {
  center: { lat: number; lng: number } | null;
  bounds?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  } | null;
}) {
  const map = useMap();
  const lastCenterRef = useRef<{ lat: number; lng: number } | null>(null);
  const lastBoundsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!map) return;

    // If we have bounds, use fitBounds to show the entire area
    if (bounds) {
      const boundsKey = JSON.stringify(bounds);
      if (boundsKey !== lastBoundsRef.current) {
        lastBoundsRef.current = boundsKey;
        const googleBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(bounds.southwest.lat, bounds.southwest.lng),
          new google.maps.LatLng(bounds.northeast.lat, bounds.northeast.lng),
        );

        // Calculate bounds size to detect if it's too small (e.g., house number, street)
        const latDiff = bounds.northeast.lat - bounds.southwest.lat;
        const lngDiff = bounds.northeast.lng - bounds.southwest.lng;
        const maxDiff = Math.max(latDiff, lngDiff);

        // Expand bounds if they're too small to avoid excessive zooming
        // MIN_BOUNDS_SIZE of 0.02 degrees is roughly 2km, which is good for streets
        const MIN_BOUNDS_SIZE = 0.02; // ~2km
        if (maxDiff < MIN_BOUNDS_SIZE) {
          // Expand bounds to a minimum size around the center
          const center = googleBounds.getCenter();
          const expandedBounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(
              center.lat() - MIN_BOUNDS_SIZE / 2,
              center.lng() - MIN_BOUNDS_SIZE / 2,
            ),
            new google.maps.LatLng(
              center.lat() + MIN_BOUNDS_SIZE / 2,
              center.lng() + MIN_BOUNDS_SIZE / 2,
            ),
          );
          map.fitBounds(expandedBounds);
        } else {
          // For larger bounds, use fitBounds but limit maximum zoom
          map.fitBounds(googleBounds);
          // Set maximum zoom level after fitBounds (use idle event to ensure it's applied)
          const idleListener = map.addListener("idle", () => {
            const currentZoom = map.getZoom();
            const MAX_ZOOM = 16; // Maximum zoom level for street-level views
            if (currentZoom && currentZoom > MAX_ZOOM) {
              map.setZoom(MAX_ZOOM);
            }
            google.maps.event.removeListener(idleListener);
          });
        }
      }
    } else if (center) {
      // Only pan if the center coordinates have actually changed
      const lastCenter = lastCenterRef.current;
      if (
        !lastCenter ||
        lastCenter.lat !== center.lat ||
        lastCenter.lng !== center.lng
      ) {
        lastCenterRef.current = center;
        map.panTo(center);
      }
    }
  }, [map, center, bounds]);

  return null;
}

function MapEventListener({
  onBoundsChange,
  onZoomChange,
  onMapIdle,
}: {
  onBoundsChange?: (b: google.maps.LatLngBounds) => void;
  onZoomChange?: (zoom: number) => void;
  onMapIdle?: () => void;
}) {
  const map = useMap();
  const lastBoundsRef = useRef<google.maps.LatLngBounds | null>(null);
  const lastZoomRef = useRef<number>(map?.getZoom() ?? 10);

  useEffect(() => {
    if (!map) return;

    const handleIdle = () => {
      const bounds = map.getBounds();
      if (!bounds) return;

      const last = lastBoundsRef.current;
      const newNE = bounds.getNorthEast();
      const newSW = bounds.getSouthWest();
      if (
        !last ||
        last.getNorthEast().lat() !== newNE.lat() ||
        last.getNorthEast().lng() !== newNE.lng() ||
        last.getSouthWest().lat() !== newSW.lat() ||
        last.getSouthWest().lng() !== newSW.lng()
      ) {
        lastBoundsRef.current = bounds;
        onBoundsChange?.(bounds);
      }

      const zoom = map.getZoom() ?? 10;
      if (zoom !== lastZoomRef.current) {
        lastZoomRef.current = zoom;
        onZoomChange?.(zoom);
      }
    };

    handleIdle();
    const listener = map.addListener("idle", () => {
      handleIdle();
      onMapIdle?.();
    });
    return () => listener.remove();
  }, [map, onBoundsChange, onZoomChange, onMapIdle]);

  return null;
}
